import type { DataRoomDealOpportunity } from "@vega-api/generated/types/dataRoomDealOpportunity";
import { DealOpportunityStatusEnum } from "@vega-api/generated/types/dealOpportunityStatusEnum";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { TrackingTag } from "@vega-modules/scenes/CoInvestmentsGP/components/TrackingTable/components/TrackingTag";

import { NdaStatusBadge } from "./NdaStatusBadge";
import { TriStateCheckbox } from "./TriStateCheckbox";
import type { TriState } from "./types";

type LpColumnHeaderProps = {
  dealOpportunity: DataRoomDealOpportunity;
  width: number;
  isResizing: boolean;
  columnTriState: TriState;
  onToggleColumn?: () => void;
  onResizeStart: (event: React.MouseEvent) => void;
  onResizeDoubleClick: () => void;
};

export function LpColumnHeader({
  dealOpportunity,
  width,
  isResizing,
  columnTriState,
  onToggleColumn,
  onResizeStart,
  onResizeDoubleClick,
}: LpColumnHeaderProps) {
  return (
    <th
      className="relative md:sticky md:top-0 md:z-30 overflow-x-clip border-b border-border/primary bg-background/primary px-2 py-1.5 text-center align-bottom"
      data-testid={`lp-column-header-${dealOpportunity.id}`}
      scope="col"
    >
      <div className="flex flex-col items-center gap-1 pb-1">
        <span
          className="body-small-regular truncate text-text/primary"
          data-testid={`lp-column-name-${dealOpportunity.id}`}
          style={{ maxWidth: width - 20 }}
          title={dealOpportunity.limitedPartner.name}
        >
          {dealOpportunity.limitedPartner.name}
        </span>
        {dealOpportunity.status === DealOpportunityStatusEnum.REJECTED ? (
          <TrackingTag
            dataTestId={`declined-tag-${dealOpportunity.id}`}
            variant="negative"
          >
            {m.coInvestments_dataRoom_declined_tag()}
          </TrackingTag>
        ) : (
          <NdaStatusBadge
            dataTestId={`nda-status-${dealOpportunity.id}`}
            numberOfNdasIssued={dealOpportunity.numberOfNdasIssued}
            numberOfNdasSigned={dealOpportunity.numberOfNdasSigned}
          />
        )}
        {onToggleColumn !== undefined && (
          <>
            <div className="h-2" />
            <TriStateCheckbox
              ariaLabel={m.coInvestments_dataRoom_toggleColumn({
                lpName: dealOpportunity.limitedPartner.name,
              })}
              dataTestId={`checkbox-column-${dealOpportunity.id}`}
              onChange={onToggleColumn}
              state={columnTriState}
            />
          </>
        )}
      </div>
      <div
        aria-hidden="true"
        className="group absolute right-0 top-0 z-30 flex h-full w-3 translate-x-1/2 cursor-col-resize items-center justify-center"
        data-testid={`lp-column-resize-handle-${dealOpportunity.id}`}
        onDoubleClick={onResizeDoubleClick}
        onMouseDown={onResizeStart}
      >
        <div
          className={cnTwMerge(
            "h-full w-0.5 rounded-full transition-colors",
            isResizing
              ? "bg-brand-default"
              : "bg-border/primary group-hover:bg-brand-hover",
          )}
        />
      </div>
    </th>
  );
}
